import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Settings, Mail, Bell, LogOut, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import EMAIL_CONFIG from "@/config/emailConfig";
import usePageTitle from "@/hooks/usePageTitle";
import EmailJsSetup from "@/components/admin/EmailJsSetup";
import VisitorEmailSettings from "@/components/admin/VisitorEmailSettings";
import InternalEmailSettings from "@/components/admin/InternalEmailSettings";

const TABS = [
  { id: "setup", label: "EmailJS Setup", icon: Settings },
  { id: "visitor", label: "Visitor Email", icon: Mail },
  { id: "internal", label: "Internal Email", icon: Bell },
];

function getSettings() {
  try {
    const saved = localStorage.getItem("eliteplus_email_settings");
    if (saved) return { ...EMAIL_CONFIG, ...JSON.parse(saved) };
  } catch {}
  return { ...EMAIL_CONFIG };
}

function saveSettings(settings) {
  localStorage.setItem("eliteplus_email_settings", JSON.stringify(settings));
}

export default function AdminDashboard() {
  usePageTitle("Email Configuration", "ElitePlus email configuration dashboard.");
  const [activeTab, setActiveTab] = useState("setup");
  const [settings, setSettings] = useState(getSettings);
  const [toast, setToast] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const auth = sessionStorage.getItem("admin_auth");
    if (auth !== EMAIL_CONFIG.adminPasswordHash) {
      navigate("/admin/login");
    }
  }, [navigate]);

  const handleSave = (updated) => {
    setSettings(updated);
    saveSettings(updated);
    showToast("Settings saved successfully");
  };

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleLogout = () => {
    sessionStorage.removeItem("admin_auth");
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white text-sm font-medium transition-all ${toast.type === "success" ? "bg-emerald-500" : "bg-red-500"}`} data-testid="admin-toast">
          {toast.message}
        </div>
      )}

      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <img src={`${process.env.PUBLIC_URL}/eliteplus_logo.png`} alt="Logo" className="w-8 h-8 object-contain" />
            <span className="text-xl font-bold text-slate-800">
              Elite<span className="text-cyan-500">Plus</span>
            </span>
            <span className="text-sm text-slate-400 ml-2 hidden sm:inline">Admin Panel</span>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="admin-logout-btn" className="text-slate-600 hover:text-red-500">
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl font-bold text-slate-800 mb-1" data-testid="admin-dashboard-title">Email Configuration</h1>
        <p className="text-slate-500 text-sm mb-4">Configure automated emails for Contact Us form submissions</p>

        <div className="p-3 mb-6 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
          <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
          <p className="text-blue-700 text-sm">
            This admin panel works entirely on static hosting. Email sending is powered by <a href="https://www.emailjs.com" target="_blank" rel="noopener noreferrer" className="underline font-medium">EmailJS</a>. Your SMTP credentials are stored securely in EmailJS — never in your website code.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 mb-8 border-b border-slate-200 pb-4">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                data-testid={`tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? "bg-cyan-500 text-white shadow-md shadow-cyan-500/25"
                    : "bg-white text-slate-600 border border-slate-200 hover:border-cyan-300 hover:text-cyan-600"
                }`}
              >
                <Icon className="w-4 h-4" /> {tab.label}
              </button>
            );
          })}
        </div>

        {activeTab === "setup" && (
          <EmailJsSetup settings={settings} onSave={handleSave} showToast={showToast} />
        )}
        {activeTab === "visitor" && (
          <VisitorEmailSettings settings={settings} onSave={handleSave} />
        )}
        {activeTab === "internal" && (
          <InternalEmailSettings settings={settings} onSave={handleSave} />
        )}
      </div>
    </div>
  );
}
